import { useEffect, useState } from "react";
import { useScrolled } from "@/hooks/useScrolled";
import { navLinks } from "@/components/feature/navLinks";
import { brandName } from "@/mocks/home";

export default function SiteHeader() {
  const scrolled = useScrolled(60);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (!menuOpen) return;
    const close = () => setMenuOpen(false);
    window.addEventListener("resize", close);
    return () => window.removeEventListener("resize", close);
  }, [menuOpen]);

  const dark = scrolled || menuOpen;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        dark
          ? "bg-background-50/95 backdrop-blur border-b border-background-200"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="h-16 md:h-20 flex items-center justify-between gap-4">
          <a
            href="#top"
            className="flex items-center gap-2.5 cursor-pointer shrink-0"
            aria-label={`${brandName} 首页`}
          >
            <span
              className={`w-9 h-9 rounded-md flex items-center justify-center text-lg font-heading font-black transition-colors ${
                dark ? "bg-primary-500 text-background-50" : "bg-background-50 text-primary-600"
              }`}
              aria-hidden="true"
            >
              瓜
            </span>
            <h1
              className={`font-heading text-xl md:text-2xl font-black tracking-tight whitespace-nowrap transition-colors ${
                dark ? "text-foreground-950" : "text-background-50"
              }`}
            >
              {brandName}
            </h1>
          </a>

          <nav aria-label="主导航" className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <a
                key={link.id}
                href={`#${link.id}`}
                className={`px-3.5 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                  dark
                    ? "text-foreground-700 hover:text-primary-600 hover:bg-primary-50"
                    : "text-background-100/90 hover:text-background-50 hover:bg-background-50/15"
                }`}
              >
                {link.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2.5">
            <span
              className={`hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-label font-medium whitespace-nowrap ${
                dark ? "bg-primary-100 text-primary-700" : "bg-background-50/20 text-background-50"
              }`}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-primary-500 pulse-dot" aria-hidden="true" />
              实时更新中
            </span>
            <a
              href="#live"
              className="hidden md:inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors hover:bg-primary-600"
            >
              进入前线
              <i className="ri-arrow-right-line" aria-hidden="true" />
            </a>
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label={menuOpen ? "关闭菜单" : "打开菜单"}
              aria-expanded={menuOpen}
              className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-md cursor-pointer transition-colors ${
                dark ? "text-foreground-900 hover:bg-background-100" : "text-background-50 hover:bg-background-50/15"
              }`}
            >
              <i className={menuOpen ? "ri-close-line text-2xl" : "ri-menu-2-line text-2xl"} aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>

      {menuOpen && (
        <div className="lg:hidden border-t border-background-200 bg-background-50">
          <nav aria-label="移动端导航" className="px-4 py-4 flex flex-col gap-1">
            {navLinks.map((link) => (
              <a
                key={link.id}
                href={`#${link.id}`}
                onClick={() => setMenuOpen(false)}
                className="px-3 py-3 rounded-md text-base font-medium text-foreground-800 cursor-pointer transition-colors hover:bg-primary-50 hover:text-primary-700"
              >
                {link.label}
              </a>
            ))}
            <a
              href="#live"
              onClick={() => setMenuOpen(false)}
              className="mt-2 inline-flex items-center justify-center gap-1.5 px-4 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors hover:bg-primary-600"
            >
              进入实时前线
              <i className="ri-arrow-right-line" aria-hidden="true" />
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}